'use client';

import { RuntimeErrorBoundary } from '@/components/widget';
import { closeSidebar } from '@/redux/store/features';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

function clearNavShellStyles() {
  if (typeof document === 'undefined') return;
  document.body.style.removeProperty('overflow');
  document.documentElement.style.removeProperty('overflow');
}

/**
 * Clears mobile-nav scroll lock on real route changes. Passes `resetKey` into the error
 * boundary so prior errors clear on navigation **without** remounting `{children}` —
 * remounting the subtree breaks App Router / RSC after browser back (empty shell).
 *
 * **Browser back/forward** can leave the client with a broken Flight shell (body only
 * shows `<div hidden><!--$--><!--/$--></div>` and no hydrated UI). `router.refresh()`
 * does not reliably fix that in dev / Turbopack. A one-shot `location.reload()` after
 * `popstate` or bfcache `pageshow` forces a full document load so the route renders again.
 */
export default function RootNavigationShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const skipNextPathCleanup = useRef(true);
  /** Avoid double reload if both `popstate` and `pageshow` fire in the same navigation. */
  const reloadLock = useRef(false);

  useEffect(() => {
    if (skipNextPathCleanup.current) {
      skipNextPathCleanup.current = false;
      return;
    }
    dispatch(closeSidebar());
    clearNavShellStyles();
  }, [pathname, dispatch]);

  useEffect(() => {
    const scheduleReloadAfterHistory = () => {
      dispatch(closeSidebar());
      clearNavShellStyles();
      if (reloadLock.current) return;
      reloadLock.current = true;
      queueMicrotask(() => {
        window.location.reload();
      });
    };

    const onPopState = () => {
      scheduleReloadAfterHistory();
    };

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        scheduleReloadAfterHistory();
      }
    };

    window.addEventListener('popstate', onPopState);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [dispatch]);

  return <RuntimeErrorBoundary resetKey={pathname}>{children}</RuntimeErrorBoundary>;
}
